import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CourseData } from "@features/CourseDetails/types/course.types";
import type { NonRegularAssignedCourseMeta } from "@features/CourseDetails/types/assignedCourses.types";

export interface CourseDetailsFormData {
	selectedCourse: string;
	morningSections: number;
	eveningSections: number;
	othersSections: number;
	morningStudents: number;
	eveningStudents: number;
	othersStudents: number;
	totalStudents: number;
	attachment: string;
	technicalCoursesCount?: number;
	vocationalCoursesCount?: number;
}

export interface LabEquipment {
	labId: number;
	equipments: Array<{
		equipmentId: number;
		availableCount: number;
	}>;
}

export interface FacultyMember {
	id?: string;
	name: string;
	qualification: number;
	designation: string;
	courses: string[];
	joiningDate: string;
	cnic: string;
	phoneNumber: string;
	salary: number | string;
	paymentMode: string;
	employmentType: string;
	attachment: string;
	profilePicture?: string; // Object key for backend
	profilePictureUrl?: string; // Blob URL for display
	gender: number;
	experienceYears?: number | string;
	// Faculty attachments
	appointmentLetter?: string; // Object key for backend
	appointmentLetterUrl?: string; // Blob URL for display
	tradeCertificate?: string; // Object key for backend
	tradeCertificateUrl?: string; // Blob URL for display
	qualificationAttachments?: string[]; // Object keys for backend
	qualificationAttachmentsUrls?: string[]; // Blob URLs for display
	createdAt?: string;
	updatedAt?: string;
}

export interface EquipmentFormData {
	[labId: string]: {
		[equipmentId: string]: number | undefined;
	};
}

// Mapping of equipmentId -> applicationCourseEquipmentId (UUID) for edit mode
export interface EquipmentIdMapping {
	[labId: string]: {
		[equipmentId: string]: string; // applicationCourseEquipmentId (UUID)
	};
}

export interface AttachmentFile {
	name: string;
	size: number;
	type: string;
	lastModified: number;
	id: string;
	objectKey?: string; // For storing S3 object key
	fileUrl?: string; // For storing file URL
}

export interface AttachmentSection {
	courseTimetable: AttachmentFile[];

	facultyAppointmentLetter: AttachmentFile[];
	facultyTradeCertificate: AttachmentFile[];
	facultyQualificationAttachments: AttachmentFile[];
}

export interface CourseDetailsState {
	courseDetails: CourseDetailsFormData;
	labs: LabEquipment[];
	equipmentFormData: EquipmentFormData;
	/** Last successfully saved available counts (edit flow) — used to skip no-op PUTs. */
	equipmentSavedBaseline: EquipmentFormData;
	/** Required counts loaded lazily per lab equipment row. */
	equipmentRequiredCounts: EquipmentFormData;
	equipmentIdMapping: EquipmentIdMapping; // Mapping of equipmentId -> applicationCourseEquipmentId (UUID)
	faculties: FacultyMember[];
	selectedFacultyUuids: string[]; // Selected faculty UUIDs from the form
	/** Preferred assessment body UUIDs for the course (registration multi-select). */
	selectedAssessmentBodyIds: string[];
	/** Dropdown options from GET /institutes/course/:uuid or institute course list (survives application-course refresh on edit). */
	catalogAssessmentBodyOptions: Array<{ uuid: string; name: string; id?: number }>;
	attachments: AttachmentSection;
	selectedCourseData: CourseData | null; // Course data from API
	perSectionMorningStudents: { [key: number]: number }; // Per-section morning students
	perSectionEveningStudents: { [key: number]: number }; // Per-section evening students
	perSectionOthersStudents: { [key: number]: number }; // Per-section others students
	resetFormTrigger: number; // Trigger to reset form
	editingCourseId: string | number | null; // ID/UUID of course being edited (null when adding new)
	isUploading: boolean; // Track file upload state
	/** When true, course dropdown shows only non-regular courses (Add non-regular flow from dashboard) */
	nonRegularCourseFlowOnly: boolean;
	/** Assigned-course metadata for non-regular flow (shifts, capacities) */
	nonRegularAssignedCourseMeta: NonRegularAssignedCourseMeta | null;
	/** Equipment step: every course lab opened and each row has a quantity (includes in-form edits). */
	allCourseLabQuantitiesEntered: boolean;
}

const initialState: CourseDetailsState = {
	courseDetails: {
		selectedCourse: "",
		morningSections: 0,
		eveningSections: 0,
		othersSections: 0,
		morningStudents: 0,
		eveningStudents: 0,
		othersStudents: 0,
		totalStudents: 0,
		attachment: "",
	},
	labs: [],
	equipmentFormData: {},
	equipmentSavedBaseline: {},
	equipmentRequiredCounts: {},
	equipmentIdMapping: {}, // Mapping of equipmentId -> applicationCourseEquipmentId (UUID)
	faculties: [],
	selectedFacultyUuids: [],
	selectedAssessmentBodyIds: [],
	catalogAssessmentBodyOptions: [],
	attachments: {
		courseTimetable: [],

		facultyAppointmentLetter: [],
		facultyTradeCertificate: [],
		facultyQualificationAttachments: [],
	},
	selectedCourseData: null,
	perSectionMorningStudents: {},
	perSectionEveningStudents: {},
	perSectionOthersStudents: {},
	resetFormTrigger: 0,
	editingCourseId: null,
	isUploading: false,
	nonRegularCourseFlowOnly: false,
	nonRegularAssignedCourseMeta: null,
	allCourseLabQuantitiesEntered: false,
};

const courseDetailsSlice = createSlice({
	name: "courseDetails",
	initialState,
	reducers: {
		setCourseDetails(state, action: PayloadAction<Partial<CourseDetailsFormData>>) {
			state.courseDetails = { ...state.courseDetails, ...action.payload };
		},
		setLabs(state, action: PayloadAction<LabEquipment[]>) {
			state.labs = action.payload;
		},
		setFaculties(state, action: PayloadAction<FacultyMember[]>) {
			state.faculties = action.payload;
		},
		addFaculty(state, action: PayloadAction<FacultyMember>) {
			state.faculties.push(action.payload);
		},
		updateFaculty(state, action: PayloadAction<{ index: number; faculty: FacultyMember }>) {
			state.faculties[action.payload.index] = action.payload.faculty;
		},
		removeFaculty(state, action: PayloadAction<number>) {
			state.faculties.splice(action.payload, 1);
		},
		setAttachments(state, action: PayloadAction<AttachmentSection>) {
			state.attachments = action.payload;
		},
		setCourseTimetableAttachments(state, action: PayloadAction<AttachmentFile[]>) {
			state.attachments.courseTimetable = action.payload;
		},

		setFacultyAppointmentLetter(state, action: PayloadAction<AttachmentFile[]>) {
			state.attachments.facultyAppointmentLetter = action.payload;
		},
		setFacultyTradeCertificate(state, action: PayloadAction<AttachmentFile[]>) {
			state.attachments.facultyTradeCertificate = action.payload;
		},
		setFacultyQualificationAttachments(state, action: PayloadAction<AttachmentFile[]>) {
			state.attachments.facultyQualificationAttachments = action.payload;
		},
		addAttachment(
			state,
			action: PayloadAction<{
				section:
					| "courseTimetable"
					| "facultyAppointmentLetter"
					| "facultyTradeCertificate"
					| "facultyQualificationAttachments";
				file: AttachmentFile;
			}>,
		) {
			state.attachments[action.payload.section].push(action.payload.file);
		},
		setSelectedCourseData(state, action: PayloadAction<CourseData | null>) {
			state.selectedCourseData = action.payload;
		},
		setEquipmentFormData(state, action: PayloadAction<EquipmentFormData>) {
			const clone: EquipmentFormData = {};
			Object.entries(action.payload).forEach(([labId, equipments]) => {
				clone[labId] = { ...(equipments ?? {}) };
			});
			state.equipmentFormData = clone;
		},
		mergeEquipmentFormData(state, action: PayloadAction<EquipmentFormData>) {
			Object.entries(action.payload).forEach(([labId, equipments]) => {
				state.equipmentFormData[labId] = {
					...(state.equipmentFormData[labId] ?? {}),
					...(equipments ?? {}),
				};
			});
		},
		setEquipmentSavedBaseline(state, action: PayloadAction<EquipmentFormData>) {
			const clone: EquipmentFormData = {};
			Object.entries(action.payload).forEach(([labId, equipments]) => {
				clone[labId] = { ...(equipments ?? {}) };
			});
			state.equipmentSavedBaseline = clone;
		},
		mergeEquipmentSavedBaselineForLab(
			state,
			action: PayloadAction<{ labId: string; equipments: Record<string, number | undefined> }>,
		) {
			const { labId, equipments } = action.payload;
			state.equipmentSavedBaseline[labId] = {
				...(state.equipmentSavedBaseline[labId] ?? {}),
				...equipments,
			};
		},
		mergeEquipmentRequiredCounts(state, action: PayloadAction<EquipmentFormData>) {
			Object.entries(action.payload).forEach(([labId, equipments]) => {
				state.equipmentRequiredCounts[labId] = {
					...(state.equipmentRequiredCounts[labId] ?? {}),
					...(equipments ?? {}),
				};
			});
		},
		setEquipmentIdMapping(state, action: PayloadAction<EquipmentIdMapping>) {
			state.equipmentIdMapping = action.payload;
		},
		mergeEquipmentIdMapping(state, action: PayloadAction<EquipmentIdMapping>) {
			Object.entries(action.payload).forEach(([labId, equipments]) => {
				state.equipmentIdMapping[labId] = {
					...(state.equipmentIdMapping[labId] ?? {}),
					...(equipments ?? {}),
				};
			});
		},
		setSelectedFacultyUuids(state, action: PayloadAction<string[]>) {
			state.selectedFacultyUuids = action.payload;
		},
		setSelectedAssessmentBodyIds(state, action: PayloadAction<string[]>) {
			state.selectedAssessmentBodyIds = action.payload;
		},
		setCatalogAssessmentBodyOptions(
			state,
			action: PayloadAction<Array<{ uuid: string; name: string; id?: number }>>,
		) {
			state.catalogAssessmentBodyOptions = action.payload;
		},
		setPerSectionMorningStudents(state, action: PayloadAction<{ [key: number]: number }>) {
			state.perSectionMorningStudents = action.payload;
		},
		setPerSectionEveningStudents(state, action: PayloadAction<{ [key: number]: number }>) {
			state.perSectionEveningStudents = action.payload;
		},
		setPerSectionOthersStudents(state, action: PayloadAction<{ [key: number]: number }>) {
			state.perSectionOthersStudents = action.payload;
		},
		setEditingCourseId(state, action: PayloadAction<string | number | null>) {
			state.editingCourseId = action.payload;
		},
		setIsUploading(state, action: PayloadAction<boolean>) {
			state.isUploading = action.payload;
		},
		setNonRegularCourseFlowOnly(state, action: PayloadAction<boolean>) {
			state.nonRegularCourseFlowOnly = action.payload;
			if (!action.payload) {
				state.nonRegularAssignedCourseMeta = null;
			}
		},
		setNonRegularAssignedCourseMeta(state, action: PayloadAction<NonRegularAssignedCourseMeta | null>) {
			state.nonRegularAssignedCourseMeta = action.payload;
		},
		setAllCourseLabQuantitiesEntered(state, action: PayloadAction<boolean>) {
			state.allCourseLabQuantitiesEntered = action.payload;
		},
		clearCourseDetails(state) {
			state.courseDetails = initialState.courseDetails;
			state.labs = [];
			state.equipmentFormData = {};
			state.equipmentSavedBaseline = {};
			state.equipmentRequiredCounts = {};
			state.equipmentIdMapping = {};
			state.perSectionMorningStudents = {};
			state.perSectionEveningStudents = {};
			state.perSectionOthersStudents = {};
			state.faculties = [];
			state.selectedFacultyUuids = [];
			state.selectedAssessmentBodyIds = [];
			state.catalogAssessmentBodyOptions = [];
			state.attachments = {
				courseTimetable: [],
				// facultyDetails: [],
				facultyAppointmentLetter: [],
				facultyTradeCertificate: [],
				facultyQualificationAttachments: [],
			};
			state.selectedCourseData = null;
			state.nonRegularAssignedCourseMeta = null;
			state.editingCourseId = null;
			state.isUploading = false;
			state.allCourseLabQuantitiesEntered = false;
		},
		resetForm(state) {
			state.resetFormTrigger = state.resetFormTrigger + 1;
		},
	},
});

export const {
	setCourseDetails,
	setLabs,
	setFaculties,
	addFaculty,
	updateFaculty,
	removeFaculty,
	setAttachments,
	setCourseTimetableAttachments,

	setFacultyAppointmentLetter,
	setFacultyTradeCertificate,
	setFacultyQualificationAttachments,
	addAttachment,
	setSelectedCourseData,
	setEquipmentFormData,
	mergeEquipmentFormData,
	setEquipmentSavedBaseline,
	mergeEquipmentSavedBaselineForLab,
	mergeEquipmentRequiredCounts,
	setEquipmentIdMapping,
	mergeEquipmentIdMapping,
	setSelectedFacultyUuids,
	setSelectedAssessmentBodyIds,
	setCatalogAssessmentBodyOptions,
	setPerSectionMorningStudents,
	setPerSectionEveningStudents,
	setPerSectionOthersStudents,
	setEditingCourseId,
	setIsUploading,
	setNonRegularCourseFlowOnly,
	setNonRegularAssignedCourseMeta,
	setAllCourseLabQuantitiesEntered,
	clearCourseDetails,
	resetForm,
} = courseDetailsSlice.actions;

export default courseDetailsSlice.reducer;
